import { PropType, computed, defineComponent, onUnmounted, ref, watch } from 'vue'
import { Store } from 'pinia'
import { useRoute } from 'vue-router'
import { gcj02ToBd09 } from '@/utils/coordinateTransform'
import { getPowerResourceType } from '@/pages/chargermap/utils/resource'
import { distanceFormat } from '@/utils/distance'
import {
  Line,
  MapViewMode,
  Marker,
  MarkerType,
  Point,
  PowerRoute,
  ZoomMode,
} from '../../contract/map'
import { ChargeMapContext } from '../chargerMap/index.model'
import { ComponentActions, ComponentState } from '../../nsh5/localStores/component'
import { ResourceIconMap, ResourceIconSizeMap, ResourceIndexMap } from '../chargerMap/mapConfig'
import { changeMarker, fitBound } from '../chargerMap/mapUtils'
import { getChargeStationDetail, getPowerSwapDetail } from '../../apis/map.api'

/**
 * 路线渲染功能模块
 */
export type RecommendRouteDetailProps = {
  onClickedMarker: (marker: Marker) => void
}
export default defineComponent({
  name: 'route-route-detail-render-view',
  props: {
    mapContextRef: {
      type: Object as PropType<ChargeMapContext>,
    },
    mapInstance: {
      type: Object,
    },
    componentStore: {
      // 用于同一项目功能模块间的通信
      type: Object as PropType<Store<string, ComponentState, {}, ComponentActions>>,
      default: null,
    },
  },

  setup: (props, { expose }) => {
    const mapContextRef = computed(() => props.mapContextRef)
    const mapInstance = computed(() => props.mapInstance)
    const route = useRoute()
    let id = ''
    const clickedMarker = ref<Marker | null>() // 选中的点
    let cacheClickedId = '' // 记录已经点击变大/已点击 的点
    const idBubbleMap = {} as { [key: string]: any } // 用于记录点击的点的id和气泡信息

    const getRouteData = () => {
      if (id) {
        const route = props.componentStore.recommendRoutes?.find((item) => item.id === id)
        if (route) {
          drawOneRecommendRoute(mapContextRef.value!, route)
        }
      }
    }

    /**
     * 点击某条推荐路线进行绘制
     */
    const drawOneRecommendRoute = (
      context: ChargeMapContext,
      recommendRoute: PowerRoute,
      strokeWeight = 2
    ) => {
      const drawPolyline: Line[] = []
      const pointList: Point[] = []
      drawPolyline.push({
        points: recommendRoute.polyline.map((pi: Point) => {
          const point = gcj02ToBd09({
            lng: Number(pi.lng),
            lat: Number(pi.lat),
          })
          pointList.push(point)
          return point
        }),
        options: {
          strokeWeight,
          strokeColor: (process.config as any).style.route_line.recommend_route,
        },
      } as Line)
      // 画线
      context.addPolyline(drawPolyline)
      const markers: Marker[] = recommendRoute.resources
        .map((item) => {
          const type = getPowerResourceType(item)
          const mode = ZoomMode.Route
          const position = item.location.split(',')
          const point = gcj02ToBd09({
            lng: Number(position[0]),
            lat: Number(position[1]),
          })
          let label = ''
          const indexLen = item.index?.length
          if (recommendRoute.distance && indexLen) {
            const lastDistanceIndex = item.index[indexLen - 1] - 1 // index索引的最后一个为distance距离的索引
            if (lastDistanceIndex > -1 && recommendRoute.distance[lastDistanceIndex] > -1) {
              // 起点资源点不需要距离
              // 当前资源点与上一个资源点的距离 size = resource.size - 1
              // distance 为-1 则表示改点没有距离数据
              const d = recommendRoute.distance[lastDistanceIndex]
              if (lastDistanceIndex == 0) {
                label += '距起点'
              }
              label += distanceFormat(d)
            }
          }
          return {
            id: item.id,
            type: MarkerType.Icon,
            loc: point,
            label: label,
            clickable: true,
            zoomable: false,
            size: ResourceIconSizeMap[mode],
            icon: ResourceIconMap[type],
            zIndex: ResourceIndexMap[type],
            data: item,
          }
        })
        .filter((val) => val.icon)
      // 添加起点
      markers.push({
        id: 'start',
        label: '起点',
        type: MarkerType.Title,
        loc: drawPolyline[0].points[0],
        size: { width: 10, height: 10 },
        clickable: false,
        icon: 'https://cdn-app.nio.com/pe/ns/h5/country/pin/origDest.png',
      })
      // 添加终点
      markers.push({
        id: 'end',
        type: MarkerType.Text,
        loc: drawPolyline[0].points[drawPolyline[0].points.length - 1],
        size: { width: 10, height: 10 },
        clickable: false,
        icon: 'https://cdn-app.nio.com/pe/ns/h5/country/pin/origDest.png',
      })
      context.addMarkers(markers)
      // 使用adjustCenterAndZoom 当前预览城市和路线距离较远时没有动画效果 所以换成 adjustCenterAndZoomWithFlyTo
      context.adjustCenterAndZoomWithFlyTo(fitBound(pointList))
    }

    onUnmounted(() => {
      mapInstance.value?.clearOverlays()
      clickedMarker.value = null
      cacheClickedId = ''
    })
    // ------------暴露出去的方法--------------
    /**
     * 通过id获取marker
     */
    props.componentStore.bus.on('onClickedMarkerByIdRecommendRouteDetail', (id: string) => {
      const marker = mapContextRef.value?.getMarkerById(id)
      if (marker) {
        onClickedMarker(mapContextRef.value?.getMarkerById(id).customData.marker)
      }
    })
    /**
     * 点击地图上的点
     * @param customMarkerData
     * @returns
     */
    const onClickedMarker = async (customMarkerData: Marker) => {
      if (cacheClickedId == customMarkerData.id) {
        // 防止对同一个点多次点击
        return
      }
      cacheClickedId = customMarkerData.id
      if (props.componentStore.viewMode === MapViewMode.RecommendRouteDetail) {
        if (!Object.prototype.hasOwnProperty.call(idBubbleMap, customMarkerData.id)) {
          // 其他地图模式(路径规划) 只变大出现气泡
          const resource: any = customMarkerData?.data
          // 兼容从gaia点进换电站，需要通过id获取详情
          const stationType = resource?.type
          const isPowerSwap = stationType
            ? stationType === 'PowerSwap'
            : customMarkerData.id.startsWith('PS')
          const res = isPowerSwap
            ? await getPowerSwapDetail(customMarkerData.id)
            : await getChargeStationDetail(customMarkerData.id)
          idBubbleMap[customMarkerData.id] = {
            name: res.data.name || '获取信息失败',
            lng: customMarkerData.loc.lng,
            lat: customMarkerData.loc.lat,
            address: res.data.address || '获取地址失败',
          }
        }
        // 设置目的地，显示小气泡
        props.componentStore.setDestLocationByPin(idBubbleMap[customMarkerData.id])
        changeMarker(mapContextRef.value!, customMarkerData.id, clickedMarker.value?.id || '')
        clickedMarker.value = customMarkerData
      }
    }
    props.componentStore.bus.on('recommendRouteDetailClickedMarker', onClickedMarker)

    expose({
      onClickedMarker,
    })

    onUnmounted(() => {
      props.componentStore.bus.off('onClickedMarkerByIdRecommendRouteDetail')
      props.componentStore.bus.off('recommendRouteDetailClickedMarker')
    })

    /**
     * 组件获取地图实例后的回调
     */
    // const onReady = () => {
    //   mapInstance.value = mapContextRef.value?.getMap()
    // }

    // watch(
    //   () => props.mapContextRef,
    //   () => {
    //     if (props.mapContextRef) {
    //       mapContextRef.value = props.mapContextRef
    //       onReady()
    //     }
    //   }
    // )

    watch(
      () => route.query.id,
      () => {
        if (props.componentStore.viewMode === MapViewMode.RecommendRouteDetail) {
          id = route.query.id as string
          getRouteData()
        } else {
          id = ''
          mapInstance.value?.clearOverlays()
        }
      },
      {
        immediate: true,
      }
    )
    return () => {}
  },
})
